// All localStorage keys used by the app. Keeping them as a frozen const
// object (a) makes autocomplete work when accessing them, and (b) surfaces
// every persistence surface in one place — handy when debugging stale state
// or planning a migration.
export const STORAGE_KEYS = {
  /** Currently-selected card list id (from `config/lists.ts`). */
  LIST_ID: "kartichki.listId",
  DIFFICULTY: "kartichki.difficulty",
  /** v2: per-list stats map, shape `{ [listId]: StatsMap }`. Migrated
   *  transparently from the old flat v1 blob on first boot (everything
   *  goes under DEFAULT_LIST_ID). The v1 key is cleaned out below. */
  STATS: "kartichki.stats.v2",
  /** Legacy (deprecated). Used by the v1→v2 migration on boot, then removed. */
  STATS_V1: "kartichki.stats.v1",
  DIR_RATIO: "kartichki.dirRatio",
  TARGET: "kartichki.targetLang",
  SOURCE: "kartichki.sourceLang",
  USER: "kartichki.userName",
  LOCALE: "kartichki.locale",
  THEME: "kartichki.theme",
  /** Active learning theme id (from config/themes.ts). Orthogonal to
   *  THEME above, which is the UI dark/light mode. */
  THEME_ID: "kartichki.themeId",
  WIZARD: "kartichki.wizardDone",
  // Self-declared starting level. Acts as a floor: the adaptive
  // estimator can still PROMOTE above it, but never below.
  LEVEL_FLOOR: "kartichki.levelFloor",
  INTRO: "kartichki.introShown",
  FONT_SCALE: "kartichki.fontScale",
  GAME_MODE: "kartichki.gameMode",
  /** JSON-encoded string[] of active class-tag filters (empty = no filter). */
  TAG_FILTER: "kartichki.tagFilter",
  /** Legacy (deprecated in 1c). Global autoplay flag from when there
   *  was only one deck. Replaced by the per-deck TTS_CONFIG bag below.
   *  Cleaned out on boot after migration. */
  TTS_AUTOPLAY: "kartichki.ttsAutoplay",
  /** Per-deck TTS config: `{ [listId]: { enabled: boolean, autoplay: boolean } }`.
   *  `enabled` = show speaker ♪ button + allow TTS at all.
   *  `autoplay` = fire TTS automatically on reveal (only meaningful when enabled).
   *  Defaults come from the deck's shape (bilingual → enabled:true). */
  TTS_CONFIG: "kartichki.ttsConfig.v1",
  /** In-progress défi (resume prompt on reload). Cleared on completion,
   *  deck switch, or game-mode change — so a stale entry can't survive
   *  into a context where it wouldn't make sense. */
  DEFI: "kartichki.defi.v1",
  /** Per-deck ring buffer of daily progression snapshots — used to
   *  compute 7-day / 30-day mastery deltas on the session-end screen.
   *  Shape: `{ [listId]: Array<{ date: "YYYY-MM-DD", masteredCount, userLevel }> }`,
   *  trimmed to PROGRESS_SNAPSHOT_CAP entries on every write. */
  PROGRESS: "kartichki.progress.v1",
} as const;

/** Legacy keys from when sheet URL + webhook credentials were user-
 *  configurable. Cleaned out on boot so localStorage stays tidy; kept
 *  here as a single source of truth for the cleanup. */
export const LEGACY_STORAGE_KEYS = [
  "kartichki.sheetUrl",
  "kartichki.webhookUrl",
  "kartichki.webhookSecret",
] as const;
